import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Icons } from '../components/Icons';
import {
  getFCMToken,
  requestNotificationPermission,
  getNotificationPermission,
  isNotificationSupported
} from '../utils/notificationManager';

function Settings() {
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('integrations');
  const [slackWebhooks, setSlackWebhooks] = useState([]);
  const [showAddWebhook, setShowAddWebhook] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState({
    inventoryAdd: true,
    inventoryUpdate: true,
    inventoryDelete: true,
    inventoryMove: true,
    quantityChange: true,
    sale: true
  });

  // 알림 설정 state
  const [notificationPreferences, setNotificationPreferences] = useState({
    browserPushEnabled: false,
    inAppEnabled: true,
    notificationTypes: {
      locationChange: true,
      quantityChange: true,
      sale: true
    }
  });
  const [notificationPermission, setNotificationPermission] = useState('default');

  // 매출 설정 state
  const [salesConfig, setSalesConfig] = useState({
    saleLocations: ['온라인', 'APF마켓', '자곡동', '행사', '기타'],
    paymentMethods: ['입금', '카드', '현금', '토스', '네이버', '페이업'],
    cardFeeRates: {
      'KB국민': 1.45, '신한': 1.45, '삼성': 1.45, '현대': 1.45, '롯데': 1.45,
      '우리': 1.45, 'NH농협': 1.45, '하나': 1.45, 'BC': 1.45,
      '토스': 3.3, '네이버페이': 3.3, '카카오페이': 2.5, '페이업': 3.4, '기타': 1.45
    }
  });

  useEffect(() => {
    fetchWebhooks();
    fetchNotificationPreferences();
    fetchSalesConfig();
    setNotificationPermission(getNotificationPermission());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'sales') {
      setActiveTab('sales');
    }
  }, [location]);

  const fetchWebhooks = async () => {
    try {
      const webhooksRef = collection(db, 'settings_webhooks');
      const snapshot = await getDocs(webhooksRef);
      const webhookList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSlackWebhooks(webhookList);
    } catch (error) {
      console.error('Webhook 불러오기 오류:', error);
    }
  };

  const handleAddWebhook = async () => {
    if (!webhookUrl.trim()) {
      alert('Webhook URL을 입력하세요.');
      return;
    }

    try {
      await addDoc(collection(db, 'settings_webhooks'), {
        url: webhookUrl,
        events: webhookEvents,
        active: true,
        createdAt: serverTimestamp(),
        createdBy: user.email
      });

      alert('✅ Slack Webhook이 추가되었습니다.');
      setShowAddWebhook(false);
      setWebhookUrl('');
      fetchWebhooks();
    } catch (error) {
      console.error('Webhook 추가 오류:', error);
      alert('Webhook 추가 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Webhook을 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'settings_webhooks', webhookId));
      alert('✅ Webhook이 삭제되었습니다.');
      fetchWebhooks();
    } catch (error) {
      console.error('Webhook 삭제 오류:', error);
      alert('Webhook 삭제 중 오류가 발생했습니다.');
    }
  };

  // 매출 설정 로드
  const fetchSalesConfig = async () => {
    try {
      const configRef = doc(db, 'settings', 'sales');
      const snapshot = await getDoc(configRef);

      if (snapshot.exists()) {
        setSalesConfig(prev => ({
          ...prev,
          ...snapshot.data()
        }));
      }
    } catch (error) {
      console.error('매출 설정 로드 오류:', error);
    }
  };

  // 매출 설정 저장
  const saveSalesConfig = async (newConfig) => {
    try {
      const configRef = doc(db, 'settings', 'sales');
      await setDoc(configRef, newConfig);
      setSalesConfig(newConfig);
      alert('✅ 매출 설정이 저장되었습니다.');
    } catch (error) {
      console.error('매출 설정 저장 오류:', error);
      alert('매출 설정 저장 중 오류가 발생했습니다.');
    }
  };

  // 배열 항목 순서 변경
  const moveItem = (key, index, direction) => {
    const newItems = [...salesConfig[key]];
    if (direction === 'up' && index > 0) {
      [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
    } else if (direction === 'down' && index < newItems.length - 1) {
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    }
    const newConfig = { ...salesConfig, [key]: newItems };
    saveSalesConfig(newConfig);
  };

  // 항목 추가
  const addItem = (key) => {
    const value = prompt('추가할 항목을 입력하세요:');
    if (!value || !value.trim()) return;
    if (salesConfig[key].includes(value)) {
      alert('이미 존재하는 항목입니다.');
      return;
    }
    const newConfig = { ...salesConfig, [key]: [...salesConfig[key], value] };
    saveSalesConfig(newConfig);
  };

  // 항목 삭제
  const removeItem = (key, value) => {
    if (!window.confirm(`'${value}' 항목을 삭제하시겠습니까?`)) return;
    const newConfig = { ...salesConfig, [key]: salesConfig[key].filter(item => item !== value) };
    saveSalesConfig(newConfig);
  };

  // 카드 수수료율 변경
  const updateCardFeeRate = (card, rate) => {
    const newConfig = {
      ...salesConfig,
      cardFeeRates: { ...salesConfig.cardFeeRates, [card]: parseFloat(rate) }
    };
    // 수수료율 변경은 즉시 저장하지 않고 사용자가 저장 버튼을 누르거나 onBlur 시 저장하도록 할 수도 있지만, 
    // 여기서는 편의상 변경 시 바로 저장하도록 함 (또는 별도 저장 버튼을 둘 수도 있음)
    // UX를 위해 여기서는 상태만 업데이트하고 저장은 별도로 하지 않거나, 디바운싱을 적용해야 하지만
    // 간단하게 구현하기 위해 상태 업데이트 후 저장을 호출함. 
    // 입력 필드에서 onChange로 호출하면 너무 잦은 저장이 발생하므로 onBlur에서 호출하는 것이 좋음.
    saveSalesConfig(newConfig);
  };

  // 카드사 추가
  const addCardCompany = () => {
    const card = prompt('추가할 카드사 이름을 입력하세요:');
    if (!card || !card.trim()) return;
    if (salesConfig.cardFeeRates[card]) {
      alert('이미 존재하는 카드사입니다.');
      return;
    }
    const newConfig = {
      ...salesConfig,
      cardFeeRates: { ...salesConfig.cardFeeRates, [card]: 1.45 } // 기본값 1.45% (중소가맹점 기준)
    };
    saveSalesConfig(newConfig);
  };

  // 카드 수수료율 기본값으로 초기화
  const resetCardFeeRates = () => {
    if (!window.confirm('카드 수수료율을 기본값으로 초기화하시겠습니까?\n(2025.8.14 기준 중소가맹점 적용)')) return;
    const defaultRates = {
      'KB국민': 1.45, '신한': 1.45, '삼성': 1.45, '현대': 1.45, '롯데': 1.45,
      '우리': 1.45, 'NH농협': 1.45, '하나': 1.45, 'BC': 1.45,
      '토스': 3.3, '네이버페이': 3.3, '카카오페이': 2.5, '페이업': 3.4, '기타': 1.45
    };
    const newConfig = { ...salesConfig, cardFeeRates: defaultRates };
    saveSalesConfig(newConfig);
  };

  // 카드사 삭제
  const removeCardCompany = (card) => {
    if (!window.confirm(`'${card}' 카드사를 삭제하시겠습니까?`)) return;
    const newRates = { ...salesConfig.cardFeeRates };
    delete newRates[card];
    const newConfig = { ...salesConfig, cardFeeRates: newRates };
    saveSalesConfig(newConfig);
  };

  // 알림 설정 로드
  const fetchNotificationPreferences = async () => {
    if (!user?.email) return;

    try {
      const preferencesRef = doc(db, 'user_notification_preferences', user.email);
      const snapshot = await getDoc(preferencesRef);

      if (snapshot.exists()) {
        setNotificationPreferences(snapshot.data());
      }
    } catch (error) {
      console.error('알림 설정 로드 오류:', error);
    }
  };

  // 알림 설정 저장
  const saveNotificationPreferences = async () => {
    if (!user?.email) return;

    try {
      const preferencesRef = doc(db, 'user_notification_preferences', user.email);
      await setDoc(preferencesRef, {
        ...notificationPreferences,
        userId: user.email,
        updatedAt: serverTimestamp()
      });

      alert('✅ 알림 설정이 저장되었습니다.');
    } catch (error) {
      console.error('알림 설정 저장 오류:', error);
      alert('알림 설정 저장 중 오류가 발생했습니다.');
    }
  };

  // 브라우저 푸시 알림 활성화
  const handleEnablePushNotifications = async () => {
    if (!isNotificationSupported()) {
      alert('이 브라우저는 알림을 지원하지 않습니다.');
      return;
    }

    try {
      // 알림 권한 요청
      const permission = await requestNotificationPermission();
      setNotificationPermission(permission);

      if (permission !== 'granted') {
        alert('알림 권한이 거부되었습니다. 브라우저 설정에서 알림 권한을 허용해주세요.');
        return;
      }

      // FCM 토큰 가져오기
      const token = await getFCMToken(user.email);

      if (token) {
        setNotificationPreferences(prev => ({
          ...prev,
          browserPushEnabled: true
        }));
        alert('✅ 브라우저 푸시 알림이 활성화되었습니다.');
      } else {
        alert('FCM 토큰을 가져올 수 없습니다. VAPID 키를 확인하세요.');
      }
    } catch (error) {
      console.error('푸시 알림 활성화 오류:', error);
      alert('푸시 알림 활성화 중 오류가 발생했습니다.');
    }
  };

  const tabs = [
    { id: 'integrations', label: '연동 설정', icon: Icons.Settings },
    { id: 'notifications', label: '알림 설정', icon: Icons.Bell },
    { id: 'sales', label: '매출 설정', icon: Icons.CurrencyDollar || Icons.Settings },
    { id: 'system', label: '시스템 관리', icon: Icons.Cog || Icons.Settings } // System management tab
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-brand-900">설정</h2>
        <p className="text-gray-500 mt-1">시스템 설정을 관리합니다.</p>
      </div>

      {/* 탭 메뉴 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 mb-6">
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-6 py-4 text-sm font-bold transition-colors ${activeTab === tab.id
                  ? 'text-brand-600 border-b-2 border-brand-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <Icon className="w-5 h-5 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 알림 설정 */}
      {activeTab === 'notifications' && (
        <div className="space-y-6">
          {/* 브라우저 푸시 알림 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                  <svg className="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">브라우저 푸시 알림</h3>
                  <p className="text-sm text-gray-500 mb-4">재고 변경사항을 브라우저 알림으로 실시간 수신합니다.</p>

                  {/* 브라우저 지원 여부 확인 */}
                  {!isNotificationSupported() ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">⚠️ 이 브라우저는 푸시 알림을 지원하지 않습니다.</p>
                    </div>
                  ) : notificationPermission === 'denied' ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-800">🚫 알림 권한이 거부되었습니다. 브라우저 설정에서 알림 권한을 허용해주세요.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition">
                        <span className="text-sm font-medium text-gray-700">브라우저 푸시 알림 활성화</span>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.browserPushEnabled}
                            onChange={(e) => {
                              if (e.target.checked) {
                                handleEnablePushNotifications();
                              } else {
                                setNotificationPreferences(prev => ({
                                  ...prev,
                                  browserPushEnabled: false
                                }));
                              }
                            }}
                            className="w-5 h-5 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                          />
                        </div>
                      </label>

                      {notificationPreferences.browserPushEnabled && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="text-sm text-green-800">✅ 브라우저 푸시 알림이 활성화되었습니다.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 앱 내 알림 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">앱 내 알림</h3>
                <p className="text-sm text-gray-500 mb-4">대시보드 내에서 알림을 확인할 수 있습니다.</p>

                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition">
                  <span className="text-sm font-medium text-gray-700">앱 내 알림 활성화</span>
                  <input
                    type="checkbox"
                    checked={notificationPreferences.inAppEnabled}
                    onChange={(e) => {
                      setNotificationPreferences(prev => ({
                        ...prev,
                        inAppEnabled: e.target.checked
                      }));
                    }}
                    className="w-5 h-5 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* 알림 종류 선택 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">받을 알림 종류</h3>
            <p className="text-sm text-gray-500 mb-4">어떤 변경사항에 대해 알림을 받을지 선택하세요.</p>

            <div className="space-y-3">
              <label className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition">
                <input
                  type="checkbox"
                  checked={notificationPreferences.notificationTypes.locationChange}
                  onChange={(e) => {
                    setNotificationPreferences(prev => ({
                      ...prev,
                      notificationTypes: {
                        ...prev.notificationTypes,
                        locationChange: e.target.checked
                      }
                    }));
                  }}
                  className="w-5 h-5 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900">📦 재고 위치 이동</span>
                  <p className="text-xs text-gray-500">제품의 보관 위치가 변경될 때 알림</p>
                </div>
              </label>

              <label className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition">
                <input
                  type="checkbox"
                  checked={notificationPreferences.notificationTypes.quantityChange}
                  onChange={(e) => {
                    setNotificationPreferences(prev => ({
                      ...prev,
                      notificationTypes: {
                        ...prev.notificationTypes,
                        quantityChange: e.target.checked
                      }
                    }));
                  }}
                  className="w-5 h-5 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900">📊 수량 변경</span>
                  <p className="text-xs text-gray-500">재고 수량이 변경될 때 알림</p>
                </div>
              </label>

              <label className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition">
                <input
                  type="checkbox"
                  checked={notificationPreferences.notificationTypes.sale}
                  onChange={(e) => {
                    setNotificationPreferences(prev => ({
                      ...prev,
                      notificationTypes: {
                        ...prev.notificationTypes,
                        sale: e.target.checked
                      }
                    }));
                  }}
                  className="w-5 h-5 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900">💰 판매 완료</span>
                  <p className="text-xs text-gray-500">제품이 판매 완료 처리될 때 알림</p>
                </div>
              </label>
            </div>
          </div>

          {/* 저장 버튼 */}
          <div className="flex justify-end">
            <button
              onClick={saveNotificationPreferences}
              className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold"
            >
              알림 설정 저장
            </button>
          </div>
        </div>
      )}

      {/* 연동 설정 */}
      {activeTab === 'integrations' && (
        <div className="space-y-6">
          {/* Slack 연동 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Slack</h3>
                  <p className="text-sm text-gray-500">재고 변경사항을 실시간으로 알림 받을 수 있습니다.</p>
                </div>
              </div>
              <button
                onClick={() => setShowAddWebhook(true)}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold text-sm flex items-center"
              >
                <Icons.Plus className="w-4 h-4 mr-2" />
                Webhook 추가
              </button>
            </div>

            {/* Webhook 목록 */}
            {slackWebhooks.length > 0 ? (
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-gray-700 mb-3">등록된 Webhook</h4>
                {slackWebhooks.map((webhook) => (
                  <div key={webhook.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className="text-xs font-mono bg-white px-3 py-1 rounded border border-gray-300 text-gray-700">
                            {webhook.url.substring(0, 50)}...
                          </span>
                          <span className={`ml-3 px-2 py-1 rounded text-xs font-bold ${webhook.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                            {webhook.active ? '활성' : '비활성'}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {webhook.events?.inventoryAdd && <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">재고 추가</span>}
                          {webhook.events?.inventoryUpdate && <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">재고 수정</span>}
                          {webhook.events?.inventoryDelete && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">재고 삭제</span>}
                          {webhook.events?.inventoryMove && <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded">재고 이동</span>}
                          {webhook.events?.quantityChange && <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">수량 변경</span>}
                          {webhook.events?.sale && <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">판매</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteWebhook(webhook.id)}
                        className="ml-4 px-3 py-1.5 bg-white hover:bg-red-50 border border-gray-300 hover:border-red-300 text-gray-700 hover:text-red-600 rounded-lg transition text-sm font-medium"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="mb-2">등록된 Webhook이 없습니다.</p>
                <p className="text-sm">Webhook을 추가하여 Slack 알림을 받아보세요.</p>
              </div>
            )}
          </div>

          {/* 안내 */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start">
              <Icons.Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-bold text-blue-900 mb-2">Slack Webhook URL 생성 방법</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Slack 워크스페이스에서 "Incoming Webhooks" 앱 설치</li>
                  <li>알림을 받을 채널 선택</li>
                  <li>생성된 Webhook URL 복사</li>
                  <li>위의 "Webhook 추가" 버튼을 클릭하여 URL 등록</li>
                </ol>
                <a
                  href="https://api.slack.com/messaging/webhooks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  자세히 보기
                  <Icons.ArrowRight className="w-4 h-4 ml-1" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 매출 설정 */}
      {activeTab === 'sales' && (
        <div className="space-y-6">
          {/* 판매 장소 관리 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">판매 장소 관리</h3>
              <button
                onClick={() => addItem('saleLocations')}
                className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-bold flex items-center"
              >
                <Icons.Plus className="w-4 h-4 mr-1" />
                추가
              </button>
            </div>
            <div className="space-y-2">
              {salesConfig.saleLocations.map((location, index) => (
                <div key={location} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-sm font-medium text-gray-700">{location}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => moveItem('saleLocations', index, 'up')}
                      disabled={index === 0}
                      className={`p-1 rounded hover:bg-gray-200 ${index === 0 ? 'text-gray-300' : 'text-gray-600'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => moveItem('saleLocations', index, 'down')}
                      disabled={index === salesConfig.saleLocations.length - 1}
                      className={`p-1 rounded hover:bg-gray-200 ${index === salesConfig.saleLocations.length - 1 ? 'text-gray-300' : 'text-gray-600'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => removeItem('saleLocations', location)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Icons.Trash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 결제 수단 관리 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">결제 수단 관리</h3>
              <button
                onClick={() => addItem('paymentMethods')}
                className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-bold flex items-center"
              >
                <Icons.Plus className="w-4 h-4 mr-1" />
                추가
              </button>
            </div>
            <div className="space-y-2">
              {salesConfig.paymentMethods.map((method, index) => (
                <div key={method} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-sm font-medium text-gray-700">{method}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => moveItem('paymentMethods', index, 'up')}
                      disabled={index === 0}
                      className={`p-1 rounded hover:bg-gray-200 ${index === 0 ? 'text-gray-300' : 'text-gray-600'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => moveItem('paymentMethods', index, 'down')}
                      disabled={index === salesConfig.paymentMethods.length - 1}
                      className={`p-1 rounded hover:bg-gray-200 ${index === salesConfig.paymentMethods.length - 1 ? 'text-gray-300' : 'text-gray-600'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => removeItem('paymentMethods', method)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Icons.Trash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 카드 수수료율 관리 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">카드 수수료율 관리 (%)</h3>
              <div className="flex gap-2">
                <button
                  onClick={resetCardFeeRates}
                  className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-bold flex items-center"
                >
                  <Icons.RefreshCw className="w-4 h-4 mr-1" />
                  기본값
                </button>
                <button
                  onClick={addCardCompany}
                  className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-bold flex items-center"
                >
                  <Icons.Plus className="w-4 h-4 mr-1" />
                  추가
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(salesConfig.cardFeeRates).map(([card, rate]) => (
                <div key={card} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-sm font-medium text-gray-700">{card}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={rate}
                      step="0.1"
                      onChange={(e) => updateCardFeeRate(card, e.target.value)}
                      className="w-20 px-2 py-1 text-right border border-gray-300 rounded text-sm focus:ring-2 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-500">%</span>
                    <button
                      onClick={() => removeCardCompany(card)}
                      className="ml-2 p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Icons.Trash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Webhook 추가 모달 */}
      {showAddWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Slack Webhook 추가</h3>
              <button
                onClick={() => setShowAddWebhook(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <Icons.X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Webhook URL */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Webhook URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://hooks.slack.com/services/..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              {/* 알림 이벤트 선택 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">알림 받을 이벤트</label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={webhookEvents.inventoryAdd}
                      onChange={(e) => setWebhookEvents({ ...webhookEvents, inventoryAdd: e.target.checked })}
                      className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">재고 추가</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={webhookEvents.inventoryUpdate}
                      onChange={(e) => setWebhookEvents({ ...webhookEvents, inventoryUpdate: e.target.checked })}
                      className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">재고 수정</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={webhookEvents.inventoryDelete}
                      onChange={(e) => setWebhookEvents({ ...webhookEvents, inventoryDelete: e.target.checked })}
                      className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">재고 삭제</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={webhookEvents.inventoryMove}
                      onChange={(e) => setWebhookEvents({ ...webhookEvents, inventoryMove: e.target.checked })}
                      className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">재고 이동 (위치 변경)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={webhookEvents.quantityChange}
                      onChange={(e) => setWebhookEvents({ ...webhookEvents, quantityChange: e.target.checked })}
                      className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">수량 변경</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={webhookEvents.sale}
                      onChange={(e) => setWebhookEvents({ ...webhookEvents, sale: e.target.checked })}
                      className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">판매</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={() => setShowAddWebhook(false)}
                className="px-6 py-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition font-bold"
              >
                취소
              </button>
              <button
                onClick={handleAddWebhook}
                className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 시스템 관리 */}
      {activeTab === 'system' && (
        <div className="space-y-6">
          {/* 데이터 일관성 검사 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">데이터 일관성 검사</h3>
                <p className="text-gray-600 mb-4">
                  전체 재고 데이터의 일관성을 검사하고 문제를 찾아냅니다.
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• location과 skuLocations 불일치 확인</li>
                  <li>• productType과 quantity/SKU 불일치 확인</li>
                  <li>• 수량과 SKU 개수 불일치 확인</li>
                  <li>• 검사 결과 CSV 다운로드 지원</li>
                </ul>
                <button
                  onClick={() => window.open('/data-consistency', '_blank')}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition font-bold"
                >
                  검사 도구 열기
                </button>
              </div>
            </div>
          </div>

          {/* 데이터 자동 수정 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">데이터 자동 수정</h3>
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 mb-4">
                  <p className="text-sm text-yellow-800 font-semibold">
                    ⚠️ 주의: 이 작업은 되돌릴 수 없습니다.
                  </p>
                </div>
                <p className="text-gray-600 mb-4">
                  검사에서 발견된 데이터 불일치를 자동으로 수정합니다.
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• 일품 제품 SKU 개수 수정</li>
                  <li>• 수량관리 제품 quantity 동기화</li>
                  <li>• SKU location 일괄 동기화</li>
                  <li>• 수정 전/후 상태 비교 및 로그</li>
                </ul>
                <button
                  onClick={() => window.open('/data-consistency-fix', '_blank')}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-bold"
                >
                  수정 도구 열기
                </button>
              </div>
            </div>
          </div>

          {/* 안내 메시지 */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
            <p className="text-sm text-blue-800">
              <strong>권장 사항:</strong> 정기적으로 (월 1회) 데이터 일관성을 검사하여 문제를 조기에 발견하세요.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
